import type { ToastSeverity } from '../components/ui/toast';

interface ToastInput {
    severity: ToastSeverity;
    message: string;
}

let dispatch: ((input: ToastInput) => void) | null = null;

/**
 * Register the provider's dispatch function. Called by ToastProvider on mount
 * (and the returned function is called on unmount). Consumers should NOT call
 * this directly — it is a private wiring helper between toast-provider.tsx
 * and the imperative `toast` API below.
 */
export function registerToastDispatch(fn: (input: ToastInput) => void): () => void {
    dispatch = fn;
    return () => {
        if (dispatch === fn) dispatch = null;
    };
}

function emit(severity: ToastSeverity, message: string): void {
    if (dispatch) {
        dispatch({ severity, message });
    } else if (typeof console !== 'undefined') {
        // No provider mounted — surface in dev so missing-provider bugs are
        // easy to spot. In production this is effectively a silent no-op.
        console.warn(`[toast] no provider mounted; dropped ${severity}: ${message}`);
    }
}

/**
 * Imperative toast API. Callable from anywhere — React component, event
 * handler, or outside-React module — provided ToastProvider is mounted
 * somewhere in the React tree.
 *
 *   toast.success('Link copied to clipboard');
 *   toast.error('Failed to save');
 *   toast.info('Your account has been deleted');
 *
 * Variants:
 *   - success — green check icon, for confirmed positive actions.
 *   - error   — red X icon, for failures.
 *   - info    — blue info icon, for neutral notifications.
 *
 * No warning variant by design; if a future surface genuinely needs one,
 * extend ToastSeverity in src/components/ui/toast.tsx and add an `emit`
 * helper here. Until then, route warnings to either info (informational)
 * or error (action required).
 */
export const toast = {
    success: (message: string) => emit('success', message),
    error: (message: string) => emit('error', message),
    info: (message: string) => emit('info', message),
};
